import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './CategoryCourses.css';
import api from '../../api';
import noCoursesImg from '../../assets/no-courses.png';
import {
  FaShoppingCart,
  FaCreditCard,
  FaHeart,
  FaPlay,
  FaStar,
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import UsersTopbar from '../UsersTopbar/UsersTopbar';
import useBlockCheck from '../../hooks/useBlockCheck';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { getDisplayPrices } from '../../utils/priceUtils';

const CategoryCourses = () => {
  useBlockCheck('user');
  const { categoryName, subcategoryName } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wishlist, setWishlist] = useState([]);
  const [cart, setCart] = useState([]);
  const [purchasedCourses, setPurchasedCourses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [coursesPerPage] = useState(9);
  const [sortOption, setSortOption] = useState('default');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Add filter states
  const [filters, setFilters] = useState({
    rating: 'All Ratings',
    language: [],
    duration: [],
    level: 'All Levels',
    subtitles: 'All',
    price: 'All Prices',
    certificationPrep: false,
  });

  const indexOfLastCourse = currentPage * coursesPerPage;
  const indexOfFirstCourse = indexOfLastCourse - coursesPerPage;
  const currentCourses = courses.slice(indexOfFirstCourse, indexOfLastCourse);

  const totalPages = Math.ceil(courses.length / coursesPerPage);

  const highlightText = (text, query) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    return text.replace(regex, '<mark class=\'highlight\'>$1</mark>');
  };

  const calculateTotalDuration = (sections) => {
    let totalSeconds = 0;
    sections.forEach((section) => {
      section.lessons.forEach((lesson) => {
        totalSeconds += lesson.duration || 0;
      });
    });
    const totalHours = Math.floor(totalSeconds / 3600);
    const totalMinutes = Math.floor((totalSeconds % 3600) / 60);
    return { totalHours, totalMinutes };
  };

  const calculateTotalDocuments = (sections) => {
    let totalDocuments = 0;
    sections.forEach((section) => {
      section.lessons.forEach((lesson) => {
        totalDocuments += lesson.documents?.length || 0;
      });
    });
    return totalDocuments;
  };

  const calculateTotalLessons = (sections) => {
    return sections.reduce((total, section) => total + (section.lessons?.length || 0), 0);
  };

  const applyFilters = (courses) => {
    return courses.filter(course => {
      // Rating filter
      if (filters.rating !== 'All Ratings') {
        const minRating = parseFloat(filters.rating);
        if (course.averageRating < minRating) return false;
      }

      // Language filter
      if (filters.language.length > 0) {
        if (!filters.language.includes(course.language)) return false;
      }

      // Duration filter
      if (filters.duration.length > 0) {
        const { totalHours } = calculateTotalDuration(course.sections || []);
        const durationMatch = filters.duration.some(range => {
          const [min, max] = range.split('-').map(Number);
          if (max) {
            return totalHours >= min && totalHours < max;
          } else {
            return totalHours >= min;
          }
        });
        if (!durationMatch) return false;
      }

      // Level filter
      if (filters.level !== 'All Levels') {
        if (course.level !== filters.level) return false;
      }

      // Price filter
      if (filters.price !== 'All Prices') {
        const prices = getDisplayPrices(course);
        // Check database values first for accurate free/paid determination
        // A course is free if: isFree flag is set, OR offerPrice is 0, OR both prices are 0
        const isCourseFree = course.isFree || 
                           course.offerPrice === 0 ||
                           (course.actualPrice === 0 && course.offerPrice === undefined);
        
        if (filters.price === 'Free' && !isCourseFree) return false;
        if (filters.price === 'Paid' && isCourseFree) return false;
        if (filters.price === 'On Sale' && prices.offerPrice >= prices.actualPrice) return false;
      }

      return true;
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get search query from URL
        const urlParams = new URLSearchParams(window.location.search);
        const searchQueryParam = urlParams.get('search') || '';
        setSearchQuery(searchQueryParam);

        const apiUrl = `${api.getAllCourses}?category=${encodeURIComponent(
          categoryName,
        )}&subcategory=${encodeURIComponent(subcategoryName)}`;

        const coursesResponse = await axios.get(apiUrl);
        let publishedCourses = (coursesResponse.data.courses || []).filter(
          (course) => course.published,
        );

        // Apply search filter if search query exists
        if (searchQueryParam) {
          publishedCourses = publishedCourses.filter((course) =>
            course.title.toLowerCase().includes(searchQueryParam.toLowerCase()),
          );
        }

        // Apply all filters
        publishedCourses = applyFilters(publishedCourses);

        let sortedCourses = [...publishedCourses];
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

        switch (sortOption) {
        case 'lowToHigh':
          // Filter out free courses, then sort by price low to high
          sortedCourses = sortedCourses.filter((course) => {
            const isCourseFree = course.isFree || 
                               course.offerPrice === 0 ||
                               (course.actualPrice === 0 && course.offerPrice === undefined);
            return !isCourseFree;
          });
          sortedCourses.sort((a, b) => {
            const pricesA = getDisplayPrices(a);
            const pricesB = getDisplayPrices(b);
            return pricesA.offerPrice - pricesB.offerPrice;
          });
          break;
        case 'highToLow':
          // Filter out free courses, then sort by price high to low
          sortedCourses = sortedCourses.filter((course) => {
            const isCourseFree = course.isFree || 
                               course.offerPrice === 0 ||
                               (course.actualPrice === 0 && course.offerPrice === undefined);
            return !isCourseFree;
          });
          sortedCourses.sort((a, b) => {
            const pricesA = getDisplayPrices(a);
            const pricesB = getDisplayPrices(b);
            return pricesB.offerPrice - pricesA.offerPrice;
          });
          break;
        case 'newest':
          // Filter courses published within last 6 months
          sortedCourses = sortedCourses.filter((course) => {
            const courseDate = new Date(course.createdAt);
            return courseDate >= sixMonthsAgo;
          });
          sortedCourses.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          break;
        case 'oldest':
          // Filter courses published more than 6 months ago
          sortedCourses = sortedCourses.filter((course) => {
            const courseDate = new Date(course.createdAt);
            return courseDate < sixMonthsAgo;
          });
          sortedCourses.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
          break;
        case 'rating':
          // Filter courses with more than 3 stars
          sortedCourses = sortedCourses.filter((course) => course.averageRating > 3);
          sortedCourses.sort((a, b) => b.averageRating - a.averageRating);
          break;
        case 'free':
          sortedCourses = sortedCourses.filter((course) => {
            const isCourseFree = course.isFree || 
                               course.offerPrice === 0 ||
                               (course.actualPrice === 0 && course.offerPrice === undefined);
            return isCourseFree;
          });
          break;
        default:
          break;
        }

        setCourses(sortedCourses);

        if (token) {
          const [cartWishlistResponse, purchasedCoursesResponse] = await Promise.all([
            axios.get(`${api.getCartAndWishlist}`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            axios.get(`${api.getPurchasedCourses}`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);

          setCart(cartWishlistResponse.data.cart || []);
          setWishlist(cartWishlistResponse.data.wishlist || []);
          setPurchasedCourses(purchasedCoursesResponse.data.courses || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryName, subcategoryName, token, sortOption, filters]);

  // Add this useEffect to handle URL changes
  useEffect(() => {
    const handleUrlChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const searchQueryParam = urlParams.get('search') || '';
      setSearchQuery(searchQueryParam);
    };

    // Listen for URL changes
    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, []);

  useEffect(() => {
    const fetchCoursesBySearch = async () => {
      try {
        setLoading(true);
        const apiUrl = `${api.getAllCourses}?category=${encodeURIComponent(
          categoryName,
        )}&subcategory=${encodeURIComponent(subcategoryName)}&search=${encodeURIComponent(searchQuery)}`;

        const coursesResponse = await axios.get(apiUrl);
        let publishedCourses = (coursesResponse.data.courses || []).filter(
          (course) => course.published,
        );

        let sortedCourses = [...publishedCourses];
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

        switch (sortOption) {
        case 'lowToHigh':
          // Filter out free courses, then sort by price low to high
          sortedCourses = sortedCourses.filter((course) => {
            const isCourseFree = course.isFree || 
                               course.offerPrice === 0 ||
                               (course.actualPrice === 0 && course.offerPrice === undefined);
            return !isCourseFree;
          });
          sortedCourses.sort((a, b) => {
            const pricesA = getDisplayPrices(a);
            const pricesB = getDisplayPrices(b);
            return pricesA.offerPrice - pricesB.offerPrice;
          });
          break;
        case 'highToLow':
          // Filter out free courses, then sort by price high to low
          sortedCourses = sortedCourses.filter((course) => {
            const isCourseFree = course.isFree || 
                               course.offerPrice === 0 ||
                               (course.actualPrice === 0 && course.offerPrice === undefined);
            return !isCourseFree;
          });
          sortedCourses.sort((a, b) => {
            const pricesA = getDisplayPrices(a);
            const pricesB = getDisplayPrices(b);
            return pricesB.offerPrice - pricesA.offerPrice;
          });
          break;
        case 'newest':
          // Filter courses published within last 6 months
          sortedCourses = sortedCourses.filter((course) => {
            const courseDate = new Date(course.createdAt);
            return courseDate >= sixMonthsAgo;
          });
          sortedCourses.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          break;
        case 'oldest':
          // Filter courses published more than 6 months ago
          sortedCourses = sortedCourses.filter((course) => {
            const courseDate = new Date(course.createdAt);
            return courseDate < sixMonthsAgo;
          });
          sortedCourses.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
          break;
        case 'rating':
          // Filter courses with more than 3 stars
          sortedCourses = sortedCourses.filter((course) => course.averageRating > 3);
          sortedCourses.sort((a, b) => b.averageRating - a.averageRating);
          break;
        case 'free':
          sortedCourses = sortedCourses.filter((course) => {
            const isCourseFree = course.isFree || 
                               course.offerPrice === 0 ||
                               (course.actualPrice === 0 && course.offerPrice === undefined);
            return isCourseFree;
          });
          break;
        default:
          break;
        }

        setCourses(sortedCourses);
      } catch (error) {
        console.error('Error fetching courses by search:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCoursesBySearch();
  }, [searchQuery, categoryName, subcategoryName, sortOption]);

  const handleAddToCart = async (courseId) => {
    try {
      await axios.post(
        `${api.addToCart}`,
        { courseId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setCart([...cart, { _id: courseId }]);
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };

  // const handleBuyNow = (courseId, price) => {
  //   navigate(`/checkout?courseId=${courseId}&price=${price}`);
  // };



  // Handle the Buy Now action
  const handleBuyNow = async (courseId) => {
    try {
      await axios.post(
        `${api.addToBuyNow}`,
        { courseId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      navigate(`/checkout?courseIds=${courseId}`);
    } catch (error) {
      console.error('Error adding to Buy Now:', error);
    }
  };

  const handleWishlistToggle = async (courseId) => {
    try {
      if (wishlist.some((item) => item._id === courseId)) {
        await axios.delete(`${api.addToWishlist}/${courseId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setWishlist((prev) => prev.filter((item) => item._id !== courseId));
      } else {
        await axios.post(
          `${api.addToWishlist}`,
          { courseId },
          {
            headers: { Authorization: `Bearer ${token}` },
          });
        setWishlist([...wishlist, { _id: courseId }]);
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => setCurrentPage(1)}
          className={currentPage === 1 ? 'cwdp-pagination-button active' : 'cwdp-pagination-button'}
        >
          1
        </button>,
      );
      if (startPage > 2) {
        pages.push(<span key="start-ellipsis" className="cwdp-ellipsis">...</span>);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={currentPage === i ? 'cwdp-pagination-button active' : 'cwdp-pagination-button'}
        >
          {i}
        </button>,
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="end-ellipsis" className="cwdp-ellipsis">...</span>);
      }
      pages.push(
        <button
          key={totalPages}
          onClick={() => setCurrentPage(totalPages)}
          className={currentPage === totalPages ? 'cwdp-pagination-button active' : 'cwdp-pagination-button'}
        >
          {totalPages}
        </button>,
      );
    }

    return (
      <div className="cwdp-pagination">
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="cwdp-pagination-button"
        >
          &lt;
        </button>
        {pages}
        <button
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          className="cwdp-pagination-button"
        >
          &gt;
        </button>
      </div>
    );
  };

  const renderCourseCardSkeleton = () =>
    Array(coursesPerPage).fill(0).map((_, index) => (
      <div key={index} className="cwdp-skeleton">
        <Skeleton height={140} />
        <div className="cwdp-skeleton-content">
          <Skeleton count={3} />
        </div>
      </div>
    ));

  return (
    <div className="cwdp-page">
      <UsersTopbar />
      <div className="cwdp-container">
        <div className="cwdp-header">
          <div>
            {/* <h1 className="cwdp-title">{subcategoryName} Courses</h1> */}
            <h1 className="cwdp-title"> Courses</h1>
            <p className="cwdp-subtitle">{courses.length} courses available</p>
          </div>
        </div>

        {/* Responsive Filter Toggle Button */}
        <button
          className={`cwdp-filter-toggle-btn${showFilters ? ' hidden' : ''}`}
          onClick={() => setShowFilters(true)}
        >
          Show Filters
        </button>

        <div className="cwdp-content">
          {/* Sidebar Filters */}
          {/* Overlay for mobile/tablet */}
          <div
            className={`cwdp-sidebar-overlay${showFilters ? ' open' : ''}`}
            onClick={() => setShowFilters(false)}
          ></div>
          <div
            className={`cwdp-sidebar${showFilters ? ' open' : ''}`}
            style={showFilters ? { zIndex: 1001 } : {}}
          >
            {/* Close button for mobile/tablet */}
            <button
              className="cwdp-filter-close-btn"
              onClick={() => setShowFilters(false)}
              type="button"
            >
              ×
            </button>
            <div className="cwdp-filter-section">
              <h3 className="cwdp-filter-heading">Filter</h3>

              <div className="cwdp-filter-group">
                <h4 className="cwdp-filter-subheading">Sort by</h4>
                <select
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                  className="cwdp-filter-select"
                >
                  <option value="default">Most Relevant</option>
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="rating">Highest Rated</option>
                  <option value="lowToHigh">Price: Low to High</option>
                  <option value="highToLow">Price: High to Low</option>
                  <option value="free">Free Courses</option>
                </select>
              </div>

              <div className="cwdp-filter-group">
                {/* <label className="cwdp-checkbox-filter">
                  <input
                    type="checkbox"
                    className="cwdp-checkbox-input"
                    checked={filters.certificationPrep}
                    onChange={(e) => setFilters(prev => ({ ...prev, certificationPrep: e.target.checked }))}
                  />
                  <span className="cwdp-checkbox-label">View certification prep courses only</span>
                </label> */}
              </div>

              <div className="cwdp-filter-group">
                <h4 className="cwdp-filter-subheading">Ratings</h4>
                <select
                  className="cwdp-filter-select"
                  value={filters.rating}
                  onChange={(e) => setFilters(prev => ({ ...prev, rating: e.target.value }))}
                >
                  <option>All Ratings</option>
                  <option>4.5 & up</option>
                  <option>4.0 & up</option>
                  <option>3.5 & up</option>
                  <option>3.0 & up</option>
                </select>
              </div>

              <div className="cwdp-filter-group">
                <h4 className="cwdp-filter-subheading">Language</h4>
                <div className="cwdp-checkbox-filters">
                  {['English', 'Hindi', 'Kannada', 'Malayalam'].map(lang => (
                    <label key={lang} className="cwdp-checkbox-filter">
                      <input
                        type="checkbox"
                        className="cwdp-checkbox-input"
                        checked={filters.language.includes(lang)}
                        onChange={(e) => {
                          setFilters(prev => ({
                            ...prev,
                            language: e.target.checked
                              ? [...prev.language, lang]
                              : prev.language.filter(l => l !== lang),
                          }));
                        }}
                      />
                      <span className="cwdp-checkbox-label">{lang}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="cwdp-filter-group">
                <h4 className="cwdp-filter-subheading">Video Duration</h4>
                <div className="cwdp-checkbox-filters">
                  {['0-2', '2-5', '5-10', '10+'].map(range => (
                    <label key={range} className="cwdp-checkbox-filter">
                      <input
                        type="checkbox"
                        className="cwdp-checkbox-input"
                        checked={filters.duration.includes(range)}
                        onChange={(e) => {
                          setFilters(prev => ({
                            ...prev,
                            duration: e.target.checked
                              ? [...prev.duration, range]
                              : prev.duration.filter(d => d !== range),
                          }));
                        }}
                      />
                      <span className="cwdp-checkbox-label">{range} hours</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="cwdp-filter-group">
                <h4 className="cwdp-filter-subheading">Level</h4>
                <select
                  className="cwdp-filter-select"
                  value={filters.level}
                  onChange={(e) => setFilters(prev => ({ ...prev, level: e.target.value }))}
                >
                  <option>All Levels</option>
                  <option>Beginner</option>
                  <option>Intermediate</option>
                  <option>Advanced</option>
                </select>
              </div>

              <div className="cwdp-filter-group">
                <h4 className="cwdp-filter-subheading">Subtitles</h4>
                <select
                  className="cwdp-filter-select"
                  value={filters.subtitles}
                  onChange={(e) => setFilters(prev => ({ ...prev, subtitles: e.target.value }))}
                >
                  <option>All</option>
                  <option>Yes</option>
                  <option>No</option>
                </select>
              </div>

              <div className="cwdp-filter-group">
                <h4 className="cwdp-filter-subheading">Price</h4>
                <select
                  className="cwdp-filter-select"
                  value={filters.price}
                  onChange={(e) => setFilters(prev => ({ ...prev, price: e.target.value }))}
                >
                  <option>All Prices</option>
                  <option>Paid</option>
                  <option>Free</option>
                  <option>On Sale</option>
                </select>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="cwdp-main">
            <div className="cwdp-search-container">
              <input
                type="text"
                placeholder="Search courses..."
                className="cwdp-search-bar"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  // Update URL without page reload
                  const newUrl = `${window.location.pathname}?search=${encodeURIComponent(e.target.value)}`;
                  window.history.pushState({}, '', newUrl);
                }}
              />
            </div>

            {loading ? (
              <div className="cwdp-grid">{renderCourseCardSkeleton()}</div>
            ) : courses.length === 0 ? (
              <div className="cwdp-empty">
                <img src={noCoursesImg} alt="No courses found" />
                <h3>No courses available in this category</h3>
                <p>Check back later or browse other categories</p>
                <button onClick={() => {
                  setSearchQuery('');
                  setSortOption('default');
                  navigate('/category/all-courses/all-courses?search=');
                }} className="cwdp-browse-btn">
                  Browse Categories
                </button>
              </div>
            ) : (
              <>
                <div className="cwdp-grid">
                  {currentCourses.map((course) => {
                    const totalLessons = calculateTotalLessons(course.sections || []);
                    const { totalHours, totalMinutes } = calculateTotalDuration(course.sections || []);
                    // const _totalDocuments = calculateTotalDocuments(course.sections || []);

                    return (
                      <div key={course._id} className="cwdp-card">
                        <div className="cwdp-card-header">
                          <div
                            className="cwdp-image-wrapper"
                            onClick={() => navigate(`/courses/${course._id}`)}
                          >
                            {course.image ? (
                              <img
                                src={`${api.img}${course.image}`}
                                alt={course.title}
                                className="cwdp-image"
                              />
                            ) : (
                              <div className="cwdp-placeholder"></div>
                            )}

                            {(() => {
                              const prices = getDisplayPrices(course);
                              return prices.offerPrice !== prices.actualPrice && (
                                <div className="cwdp-discount">
                                  {Math.round(
                                    ((prices.actualPrice - prices.offerPrice) /
                                      prices.actualPrice) *
                                    100,
                                  )}
                                  % OFF
                                </div>
                              );
                            })()}
                          </div>
                          {!purchasedCourses.some(
                            (item) => item._id === course._id,
                          ) && (
                            <button
                              className={`cwdp-wishlist ${wishlist.some((item) => item._id === course._id)
                                ? 'active'
                                : ''
                              }`}
                              onClick={() => handleWishlistToggle(course._id)}
                            >
                              <FaHeart />
                            </button>
                          )}
                        </div>

                        <div className="cwdp-card-body" onClick={() => navigate(`/courses/${course._id}`)}>

                          <p className="cwdp-description">
                            <strong>{totalHours}.{totalMinutes} hours on-demand video</strong>
                          </p>
                          {/* <p className="cwdp-description">
                            <strong>{totalDocuments} downloadable resources</strong>
                          </p> */}
                          <p className="cwdp-description">
                            <strong>Total {course?.sections?.length || 0} Sections & {totalLessons} Lessons</strong>

                          </p>
                          <div className="cwdp-play-overlay">
                            <FaPlay className="cwdp-play-icon" />
                          </div>
                        </div>

                        <div className="cwdp-card-footer">
                          <div className="cwdp-card-footer-title">
                            <h3
                              className="cwdp-title-text"
                              dangerouslySetInnerHTML={{ __html: highlightText(course.title, searchQuery) }}
                            ></h3>
                            <div className="cwdp-meta">
                              <div className="cwdp-rating">
                                <div className="cwdp-stars">
                                  {Array.from({ length: 5 }).map((_, i) => (
                                    <FaStar
                                      key={i}
                                      className={i < Math.round(course.averageRating) ? 'filled' : ''}
                                    />
                                  ))}
                                </div>
                                <span>({course.ratingCount || 0})</span>
                              </div>
                            </div>

                            <div className="cwdp-price">
                              {(() => {
                                const prices = getDisplayPrices(course);
                                return prices.offerPrice === 0 ? (
                                  <span className="cwdp-price-free">Free</span>
                                ) : (
                                  <>
                                    <span className="cwdp-price-current">
                                      ₹{prices.offerPrice}
                                    </span>
                                    {prices.offerPrice !== prices.actualPrice && (
                                      <span className="cwdp-price-original">
                                        ₹{prices.actualPrice}
                                      </span>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                          <div className="cwdp-card-footer-buttons">
                            {purchasedCourses.some(
                              (item) => item._id === course._id,
                            ) ? (
                                <button
                                  className="cwdp-continue-btn"
                                  onClick={() => navigate(`/course/${course._id}`)}
                                >
                                Continue Learning
                                </button>
                              ) : (
                                <>
                                  {cart.some((item) => item._id === course._id) ? (
                                    <button
                                      className="cwdp-cart-btn"
                                      onClick={() => navigate('/cart')}
                                    >
                                      <FaShoppingCart /> Go to Cart
                                    </button>
                                  ) : (
                                    <button
                                      className="cwdp-btn"
                                      onClick={() => handleAddToCart(course._id)}
                                    >
                                      <FaShoppingCart /> Add to Cart
                                    </button>
                                  )}
                                  <button
                                    className="cwdp-buy-btn"

                                    onClick={() => handleBuyNow(course._id, course.price)}
                                  >
                                    <FaCreditCard /> Buy Now
                                  </button>
                                </>
                              )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {renderPagination()}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryCourses;